// US-DEPOSIT-06 + US-WITHDRAWAL-04 -- child sees their own pending requests
// (deposits + withdrawals) and can cancel any that haven't been decided yet.
//
// This screen mirrors PendingApprovalsScreen's discriminated-union shape, but
// the entries don't carry a childDisplayName (the child knows who they are),
// and the only action available is Cancel.

import {
  type ChoreEntry,
  type MyPendingDepositEntry,
  type MyPendingTransferEntry,
  type MyPendingWithdrawalEntry,
  choresList,
  depositCancel,
  myPendingDepositsList,
  myPendingTransfersList,
  myPendingWithdrawalsList,
  transferCancel,
  withdrawalCancel,
} from '@chorepop/core/client';
import { type Component, For, Match, Show, Switch, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { TransientBanner } from '../components/TransientBanner';

type Props = {
  sessionId: string;
  /** The signed-in child.  Chores use this explicit target because the
   * command also supports guardian cross-child views. */
  childAccountId: number;
  currencyCode: string;
  onBack: () => void;
  onViewChores: () => void;
};

/** A unified queue entry -- discriminated by `kind`. */
type RequestItem =
  | { kind: 'deposit'; id: number; at: string; entry: MyPendingDepositEntry }
  | { kind: 'withdrawal'; id: number; at: string; entry: MyPendingWithdrawalEntry }
  /** US-BUCKET-11 -- child cancels their own pending bucket transfer. */
  | { kind: 'transfer'; id: number; at: string; entry: MyPendingTransferEntry }
  /** Chore claims are waiting on a guardian too.  They are read-only here;
   * the child can see or resubmit them from the chore list. */
  | { kind: 'chore'; id: number; at: string; entry: ChoreEntry };

function rowKey(item: RequestItem): string {
  return `${item.kind}:${item.id}`;
}

export const MyPendingRequestsScreen: Component<Props> = (props) => {
  const [queue, { refetch }] = createResource(async (): Promise<RequestItem[]> => {
    const [deposits, withdrawals, transfers, chores] = await Promise.all([
      myPendingDepositsList({ sessionId: props.sessionId }),
      myPendingWithdrawalsList({ sessionId: props.sessionId }),
      myPendingTransfersList({ sessionId: props.sessionId }),
      choresList({ sessionId: props.sessionId, childAccountId: props.childAccountId }),
    ]);
    if (!deposits.ok) throw new Error(deposits.error.message);
    if (!withdrawals.ok) throw new Error(withdrawals.error.message);
    if (!transfers.ok) throw new Error(transfers.error.message);
    if (!chores.ok) throw new Error(chores.error.message);

    const items: RequestItem[] = [
      ...deposits.value.map<RequestItem>((d) => ({
        kind: 'deposit',
        id: d.depositId,
        at: d.requestedAtUtc,
        entry: d,
      })),
      ...withdrawals.value.map<RequestItem>((w) => ({
        kind: 'withdrawal',
        id: w.withdrawalId,
        at: w.requestedAtUtc,
        entry: w,
      })),
      ...transfers.value.map<RequestItem>((t) => ({
        kind: 'transfer',
        id: t.bucketTransferId,
        at: t.requestedAtUtc,
        entry: t,
      })),
      ...chores.value
        .filter((chore) => chore.hasOpenClaim)
        .map<RequestItem>((chore) => ({
          kind: 'chore',
          id: chore.choreId,
          // ChoreEntry does not expose the completion timestamp.  Its
          // creation date is a stable ordering fallback; the rendered state
          // is what matters, and this avoids pretending we know a timestamp.
          at: chore.createdAtUtc,
          entry: chore,
        })),
    ];
    // Newest-first -- child wants to find what they just submitted, not work
    // through a queue.
    items.sort((a, b) => {
      if (a.at !== b.at) return b.at.localeCompare(a.at);
      return rowKey(b).localeCompare(rowKey(a));
    });
    return items;
  });

  const [busyKey, setBusyKey] = createSignal<string | null>(null);
  const [flash, setFlash] = createSignal<string | null>(null);
  const [rowError, setRowError] = createSignal<{ key: string; msg: string } | null>(null);
  const [confirming, setConfirming] = createSignal<RequestItem | null>(null);

  const friendlyConflict = 'Your guardian already decided this -- refreshing.';

  const cancelConfirmed = async () => {
    const item = confirming();
    if (!item) return;
    setConfirming(null);
    const key = rowKey(item);
    setBusyKey(key);
    setRowError(null);
    try {
      if (item.kind === 'chore') {
        return;
      }
      if (item.kind === 'deposit') {
        const result = await depositCancel({
          sessionId: props.sessionId,
          depositId: item.entry.depositId,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          void refetch();
          return;
        }
        setFlash(
          `Cancelled your ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)} deposit.`,
        );
      } else if (item.kind === 'withdrawal') {
        const result = await withdrawalCancel({
          sessionId: props.sessionId,
          withdrawalId: item.entry.withdrawalId,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          void refetch();
          return;
        }
        setFlash(
          `Cancelled your request for ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)} from ${item.entry.bucketName}.`,
        );
      } else {
        // US-BUCKET-11 -- cancel a pending bucket transfer.
        const result = await transferCancel({
          sessionId: props.sessionId,
          bucketTransferId: item.entry.bucketTransferId,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          void refetch();
          return;
        }
        setFlash(
          item.entry.deactivateSourceOnApprove
            ? `Cancelled your request to delete ${item.entry.sourceBucketName}.`
            : `Cancelled your transfer of ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)} from ${item.entry.sourceBucketName} to ${item.entry.targetBucketName ?? '(unknown)'}.`,
        );
      }
      void refetch();
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <ActionScreen eyebrow="Waiting on Mum & Dad" title="My requests" onBack={props.onBack}>
      <Show when={flash()}>
        {(msg) => (
          <TransientBanner class="home-flash" message={msg()} onDismiss={() => setFlash(null)} />
        )}
      </Show>

      <Switch>
        <Match when={queue.loading}>
          <p>Loading…</p>
        </Match>
        <Match when={queue.error}>
          <p class="form-error" role="alert">
            Couldn't load your requests: {(queue.error as Error).message}
          </p>
        </Match>
        <Match when={(queue() ?? []).length === 0}>
          <p class="history-empty">You don't have any pending requests right now.</p>
        </Match>
        <Match when={(queue() ?? []).length > 0}>
          <ul class="history-list action-request-list">
            <For each={queue() ?? []}>
              {(item) => (
                <RequestRow
                  item={item}
                  currencyCode={props.currencyCode}
                  busy={busyKey() === rowKey(item)}
                  errorMessage={rowError()?.key === rowKey(item) ? (rowError()?.msg ?? null) : null}
                  onCancel={() => setConfirming(item)}
                  onViewChores={props.onViewChores}
                />
              )}
            </For>
          </ul>
        </Match>
      </Switch>

      <Show when={confirming()} keyed>
        {(item) =>
          item.kind === 'chore' ? null : (
            <div
              class="confirm-backdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget && busyKey() === null) {
                  setConfirming(null);
                }
              }}
            >
              <div class="confirm-dialog" role="dialog" aria-modal="true">
                <CancelDialogBody item={item} currencyCode={props.currencyCode} />
                <div class="confirm-buttons">
                  <button
                    type="button"
                    class="ghost"
                    onClick={() => setConfirming(null)}
                    disabled={busyKey() !== null}
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    class="danger"
                    onClick={cancelConfirmed}
                    disabled={busyKey() !== null}
                  >
                    {busyKey() !== null ? 'Cancelling…' : 'Yes, cancel it'}
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </Show>
    </ActionScreen>
  );
};

const CancelDialogBody: Component<{ item: RequestItem; currencyCode: string }> = (props) => (
  <Switch>
    <Match when={props.item.kind === 'deposit'}>
      <h2>
        Cancel your{' '}
        <strong>
          {formatAmount(
            (props.item.entry as MyPendingDepositEntry).amountMinorUnits,
            props.currencyCode,
          )}
        </strong>{' '}
        deposit request?
      </h2>
      <p>Once cancelled, your guardian won't see it any more. You can submit a new one any time.</p>
    </Match>
    <Match when={props.item.kind === 'withdrawal'}>
      <h2>
        Cancel your request for{' '}
        <strong>
          {formatAmount(
            (props.item.entry as MyPendingWithdrawalEntry).amountMinorUnits,
            props.currencyCode,
          )}
        </strong>{' '}
        from {(props.item.entry as MyPendingWithdrawalEntry).bucketName}?
      </h2>
      <p>Your bucket balance won't change. You can request again any time.</p>
    </Match>
    <Match when={props.item.kind === 'transfer'}>
      <Show
        when={(props.item.entry as MyPendingTransferEntry).deactivateSourceOnApprove}
        fallback={
          <>
            <h2>
              Cancel your transfer of{' '}
              <strong>
                {formatAmount(
                  (props.item.entry as MyPendingTransferEntry).amountMinorUnits,
                  props.currencyCode,
                )}
              </strong>{' '}
              from {(props.item.entry as MyPendingTransferEntry).sourceBucketName} to{' '}
              {(props.item.entry as MyPendingTransferEntry).targetBucketName ?? '(unknown)'}?
            </h2>
            <p>Your bucket balances won't change. You can request the move again any time.</p>
          </>
        }
      >
        <h2>
          Cancel your request to delete{' '}
          {(props.item.entry as MyPendingTransferEntry).sourceBucketName}?
        </h2>
        <p>The bucket stays active and any balance stays where it is.</p>
      </Show>
    </Match>
  </Switch>
);

const RequestRow: Component<{
  item: RequestItem;
  currencyCode: string;
  busy: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onViewChores: () => void;
}> = (props) => {
  const formatDate = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const amount = () =>
    props.item.kind === 'chore'
      ? props.item.entry.rewardMinorUnits
      : props.item.entry.amountMinorUnits;

  const title = () => {
    if (props.item.kind === 'deposit') {
      return `Deposit · ${(props.item.entry as MyPendingDepositEntry).sourceLabel}`;
    }
    if (props.item.kind === 'withdrawal') {
      return `Withdrawal · from ${(props.item.entry as MyPendingWithdrawalEntry).bucketName}`;
    }
    if (props.item.kind === 'chore') {
      return `Chore complete · ${props.item.entry.choreName}`;
    }
    // transfer
    {
      const e = props.item.entry as MyPendingTransferEntry;
      if (e.deactivateSourceOnApprove) {
        return e.targetBucketName !== null
          ? `Delete bucket · ${e.sourceBucketName} -> ${e.targetBucketName}`
          : `Delete bucket · ${e.sourceBucketName} (empty)`;
      }
      return `Move · ${e.sourceBucketName} -> ${e.targetBucketName ?? '(unknown)'}`;
    }
  };

  const kindLabel = () => {
    switch (props.item.kind) {
      case 'deposit':
        return 'DEPOSIT';
      case 'withdrawal':
        return 'WITHDRAWAL';
      case 'transfer':
        return (props.item.entry as MyPendingTransferEntry).deactivateSourceOnApprove
          ? 'DELETE BUCKET'
          : 'TRANSFER';
      case 'chore':
        return 'CHORE';
    }
  };

  const note = () => {
    if (props.item.kind === 'deposit') return props.item.entry.note;
    if (props.item.kind === 'withdrawal') return props.item.entry.reasonNote;
    if (props.item.kind === 'chore') return 'Your parent is reviewing this completion.';
    return (props.item.entry as MyPendingTransferEntry).note;
  };

  // Transfers are neither deposits (+) nor withdrawals (−); they're a
  // sideways move between buckets. The sign character is purely
  // visual scaffolding from the existing layout, so for transfers we
  // render an arrow instead -- same span position so layout is
  // unchanged.
  const signLabel = () => {
    switch (props.item.kind) {
      case 'deposit':
        return '+';
      case 'withdrawal':
        return '−';
      case 'transfer':
        return '↔';
      case 'chore':
        return '✓';
    }
  };
  const signClass = () => {
    switch (props.item.kind) {
      case 'deposit':
        return 'history-sign history-sign--plus';
      case 'withdrawal':
        return 'history-sign history-sign--minus';
      case 'transfer':
        return 'history-sign';
      case 'chore':
        return 'history-sign history-sign--plus';
    }
  };

  return (
    <li class={`history-row action-request-row history-row--${props.item.kind}`}>
      <div class="history-row-leading">
        <span class={signClass()}>{signLabel()}</span>
        <span class="history-amount">{formatAmount(amount(), props.currencyCode)}</span>
      </div>
      <div class="history-row-body">
        <div class="history-row-title">{title()}</div>
        <Show when={note()}>{(n) => <div class="history-row-note">"{n()}"</div>}</Show>
        <div class="history-row-allocations">
          <span class="history-chip">{kindLabel()}</span>
          <Show when={props.item.kind === 'withdrawal'}>
            <span class="history-chip">
              {(props.item.entry as MyPendingWithdrawalEntry).reasonLabel}
            </span>
          </Show>
        </div>
        <div class="history-row-meta">{formatDate(props.item.at)}</div>
        <Show when={props.errorMessage}>
          {(msg) => (
            <div class="form-error" role="alert" style="margin-top: 8px;">
              {msg()}
            </div>
          )}
        </Show>
        <Show
          when={props.item.kind === 'chore'}
          fallback={
            <div style="margin-top: 8px; display: flex; gap: 8px;">
              <button
                type="button"
                class="ghost"
                disabled={props.busy}
                onClick={() => props.onCancel()}
              >
                {props.busy ? 'Cancelling…' : 'Cancel'}
              </button>
            </div>
          }
        >
          <div style="margin-top: 8px; display: flex; gap: 8px;">
            <button type="button" class="ghost" onClick={() => props.onViewChores()}>
              View chore
            </button>
          </div>
        </Show>
      </div>
    </li>
  );
};

function formatAmount(minor: number, currencyCode: string): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(major);
  } catch {
    return `${currencyCode} ${major.toFixed(2)}`;
  }
}
